import Contract from "domain/contract";
import Offer from "domain/offer";
import { isAllowed, OfferStatus } from "domain/offerStatus";
import { Offering } from "features/settings/offerings/offering";
import { isPartiallyFilled } from "./orderStatus";
import AddOfferModel from "./addOfferModel";
import { OfferTypeEnum, toContractType } from "domain/offerType";
import { getLastMonthEndDate, getNthLastWeekDay } from "common/formatters";
import { sumArray } from "common/decimal";
import { DeliveryMethod } from "domain/deliveryMethod";
import * as NP from "number-precision";
import { OrderType } from "domain/orderType";
import { UserConfiguration } from "features/login/loggedInUser";
import { isFilledOrPartiallyFilled } from "./orderStatus";
import offerTypeHelper from "domain/offerTypeHelper";
import { ProducerAccount } from "features/settings/producers/producerAccount";
import { localizeOfferContract } from "features/localization/localizeService";
import PageFilter from "features/pageFilter/pageFilter";
import { PositionSetting } from "domain/positionSetting";
import Merchant from "domain/merchant";
import { HedgingSettings } from "features/settings/hedging/hedgingSettings";
import FcmAccount from "features/fcmAccounts/fcmAccount";
import calculateNumberOfContracts from "./contractCalculator";
import FuturesContract from "domain/futuresContract";
import { findHedgeSetting } from "features/settings/hedging/helpers";
import DeliveryPeriod from "domain/deliveryPeriod";
import { getFeeValue } from "domain/fee";
import * as _ from "lodash";
import deliveryPeriodService from "features/deliveryPeriods/deliveryPeriod.service";
import Commodity from "domain/commodity";

export function allowEdit(offer: Offer, isCustomer: boolean, userConfiguration: UserConfiguration, offerings: Offering[], customerAccounts: ProducerAccount[]): boolean {

    if (!userConfiguration.canMakeOffer) {
        return false;
    }
    if ((offer.customerWillSell && !userConfiguration.canSell) ||
        (!offer.customerWillSell && !userConfiguration.canBuy)) {
        return false;
    }

    if (isCustomer && offer.customerAccount != null &&
        !customerAccounts.some(x => x.accountId === offer.customerAccount?.accountId && x.canManage)) {
        return false;
    }

    if (isCustomer && offer.status === OfferStatus.Pending) {
        return false;
    }

    if (!isOfferTypeAllowed(isCustomer, offer.type, userConfiguration)) {
        return false;
    }

    if (offer.orderType === OrderType.Tas && isFilledOrPartiallyFilled(offer.order?.status)) {
        return false;
    }

    const isPartialFill = isPartiallyFilled(offer.order?.status);
    if (!isPartialFill && isAllowed(offer, OfferStatus.Working, OfferStatus.Pending)
        && (!isCustomer ||
            (offerings.some(o => o.location.id === offer.location?.id && o.commodity.id === offer.deliveryPeriod.commodity.id && o.offerTypes.includes(offer.type)))
        )
    ) {
        return true;
    }
    return false;
}

export function getDelayedPriceContractDisplayText(contract: Contract): string {
    if (contract.erpId == null) return contract.displayId;
    return `${contract.displayId} (${contract.erpDisplayId})`;
}

export function getFuturesContract(locationId: Nullish<string>, deliveryPeriod: Nullish<DeliveryPeriod>): FuturesContract | null {
    const bid = deliveryPeriod?.bids?.find(x => x.location?.id === locationId);
    if (bid?.useSecondarySymbol) {
        return deliveryPeriod?.secondaryContract ?? null;
    }
    return deliveryPeriod?.contract ?? null;
}

async function getFirstDeliveryPeriod(contract: Contract): Promise<DeliveryPeriod | null> {
    const deliveryPeriodsResponse = await deliveryPeriodService.listDeliveryPeriods(contract.commodity.id);
    return _.sortBy(deliveryPeriodsResponse?.deliveryPeriods, ['start']).find(m => m.start >= contract.deliveryPeriodStartDate) ?? null;
}

export async function getModelForSetFutures(contract: Contract, deliveryPeriod?: Nullish<DeliveryPeriod>): Promise<AddOfferModel> {
    if (deliveryPeriod == null) {
        deliveryPeriod = await getFirstDeliveryPeriod(contract);
    }
    return {
        contractId: contract.internalId,
        displayId: contract.displayId,
        offerType: OfferTypeEnum.FixFuturesOnBasis,
        commodity: contract.commodity,
        deliveryMethod: contract.deliveryMethod,
        deliveryPeriod: deliveryPeriod,
        deliveryTerms: contract.deliveryTerms,
        futuresContract: contract.futuresContract,
        deliveryPeriodStartDate: contract.deliveryPeriodStartDate,
        deliveryPeriodEndDate: contract.deliveryPeriodEndDate,
        expirationDate: contract.finalPricingDate,
        finalPricingDate: contract.finalPricingDate,
        location: contract.location,
        region: contract.region,
        contractLocation: contract.contractLocation,
        inventoryLocation: contract.inventoryLocation,
        customer: contract.producer,
        customerAccount: contract.customerAccount,
        customerWillSell: contract.customerWillSell,
        quantity: contract.quantityContracted,
        basis: contract.basis,
        isBasisFixed: contract.isBasisLocked,
        paymentOption: contract.paymentOption,
        deferredPayDate: contract.deferredPayDate,
        fees: contract.fees,
        freightFee: contract.freightFee,
        customFields: contract.customFields
    };
}

export async function getModelForSetBasis(contract: Contract, deliveryPeriod?: Nullish<DeliveryPeriod>): Promise<AddOfferModel> {
    if (deliveryPeriod == null) {
        deliveryPeriod = await getFirstDeliveryPeriod(contract);
    }
    return {
        contractId: contract.internalId,
        displayId: contract.displayId,
        offerType: OfferTypeEnum.FixBasisOnHta,
        commodity: contract.commodity,
        deliveryMethod: contract.deliveryMethod,
        deliveryPeriod: deliveryPeriod,
        deliveryTerms: contract.deliveryTerms,
        futuresContract: contract.futuresContract,
        deliveryPeriodStartDate: contract.deliveryPeriodStartDate,
        deliveryPeriodEndDate: contract.deliveryPeriodEndDate,
        location: contract.location,
        contractLocation: contract.contractLocation,
        inventoryLocation: contract.inventoryLocation,
        region: contract.region,
        customer: contract.producer,
        customerAccount: contract.customerAccount,
        customerWillSell: contract.customerWillSell,
        quantity: contract.quantityContracted,
        futuresPrice: contract.futuresPrice,
        basis: contract.offer.currentBasis,
        isBasisFixed: false,
        paymentOption: contract.paymentOption,
        deferredPayDate: contract.deferredPayDate,
        expirationDate: contract.finalPricingDate,
        finalPricingDate: contract.finalPricingDate,
        freightFee: contract.freightFee,
        fees: contract.fees,
        customFields: contract.customFields
    };
}

export function getModelForAddBasisOffer(contract: Contract): AddOfferModel {
    return {
        contractId: contract.internalId,
        displayId: contract.displayId,
        offerType: OfferTypeEnum.FixBasisOnHta,
        commodity: contract.commodity,
        deliveryMethod: contract.deliveryMethod,
        deliveryPeriod: contract.deliveryPeriod,
        deliveryTerms: contract.deliveryTerms,
        futuresContract: contract.futuresContract,
        location: contract.location,
        contractLocation: contract.contractLocation,
        region: contract.region,
        customer: contract.producer,
        customerAccount: contract.customerAccount,
        customerWillSell: contract.customerWillSell,
        quantity: contract.quantityContracted,
        futuresPrice: contract.futuresPrice,
        basis: contract.offer.currentBasis,
        isBasisFixed: true,
        paymentOption: contract.paymentOption,
        deferredPayDate: contract.deferredPayDate,
        expirationDate: contract.finalPricingDate,
        finalPricingDate: contract.finalPricingDate,
        freightFee: contract.freightFee,
        fees: contract.fees,
        customFields: contract.customFields
    };
}

export function getModelForConvertBushelOnly(contract: Contract): AddOfferModel {
    return {
        contractId: contract.internalId,
        displayId: contract.displayId,
        offerType: OfferTypeEnum.BushelOnlyContract,
        commodity: contract.commodity,
        deliveryMethod: contract.deliveryMethod,
        deliveryPeriod: contract.deliveryPeriod,
        deliveryTerms: contract.deliveryTerms,
        deliveryPeriodStartDate: contract.deliveryPeriodStartDate,
        deliveryPeriodEndDate: contract.deliveryPeriodEndDate,
        futuresContract: contract.futuresContract,
        location: contract.location,
        contractLocation: contract.contractLocation,
        region: contract.region,
        customer: contract.producer,
        customerAccount: contract.customerAccount,
        customerWillSell: contract.customerWillSell,
        quantity: contract.quantityContracted,
        futuresPrice: contract.futuresPrice,
        basis: contract.basis,
        bushelOnlyContract: contract,
        isBasisFixed: false,
        paymentOption: contract.paymentOption,
        deferredPayDate: contract.deferredPayDate,
        expirationDate: contract.finalPricingDate,
        finalPricingDate: contract.finalPricingDate,
        freightFee: contract.freightFee,
        fees: contract.fees,
        customFields: contract.customFields
    };
}

export function getModelForDelayedPrice(contract: Contract): AddOfferModel {
    return {
        contractId: contract.internalId,
        displayId: contract.displayId,
        offerType: OfferTypeEnum.Cash,
        commodity: contract.commodity,
        location: contract.location,
        contractLocation: contract.contractLocation,
        region: contract.region,
        customer: contract.producer,
        customerAccount: contract.customerAccount,
        customerWillSell: contract.customerWillSell,
        quantity: contract.quantityContracted,
        delayedPriceContract: contract,
        remainingQuantity: contract.remainingQuantity,
        paymentOption: contract.paymentOption,
        deferredPayDate: contract.deferredPayDate,
        fees: contract.fees,
        customFields: contract.customFields
    };
}

export function isCashOfferWithOpenStorage(offer: NullishPartial<Offer>) {
    return offerTypeHelper.isCash(offer.type) && offer.deliveryMethod === DeliveryMethod.OpenStorage;
}

export function getSecondLastBusinessDayOfLastMonth(date: Nullish<Date>): Date {
    const endOfLastMonth = getLastMonthEndDate(date);
    return getNthLastWeekDay(endOfLastMonth, 2);
}

export function getRemainingQuantityLabel(offerType: Nullish<OfferTypeEnum>, deliveryMethod: Nullish<DeliveryMethod>): string {
    if (([OfferTypeEnum.FixBasisOnHta, OfferTypeEnum.FixFuturesOnBasis] as Nullish<OfferTypeEnum>[]).includes(offerType)
        || deliveryMethod === DeliveryMethod.DelayedPrice) {
        return localizeOfferContract("OfferHelper_Value_UnpricedQuantity");
    }
    else {
        return localizeOfferContract("OfferHelper_Value_RemainingQuantity");
    }
}

export function getTotalFees(offer: NullishPartial<Offer>) {
    const extendedFeesSum = sumArray(offer.fees?.map(m => getFeeValue(m, offer.deliveryPeriodStartDate)));
    const totalFees = NP.plus(extendedFeesSum, offer.freightFee ?? 0);
    return totalFees;
}

function isOfferTypeAllowed(isCustomer: boolean, offerType: OfferTypeEnum, userConfiguration: UserConfiguration) {
    return isCustomer || userConfiguration.allowedContractTypes.includes(toContractType(offerType))
}

export function getFinalPrice(offer: NullishPartial<Offer>) {
    const price = offerTypeHelper.isHta(offer.type) && offer.orderType !== OrderType.Tas
        ? offer.futuresPrice
        : offer.type === OfferTypeEnum.BasisOffer
            ? offer.basis
            : offer.cashPrice;
    const totalFees = getTotalFees(offer);

    // price + premium - fees
    return NP.minus(NP.plus(price ?? 0, offer.deliveryPeriod?.premium ?? 0), totalFees);
}

export function filterOffers(offers: Offer[], pageFilter: PageFilter): Offer[] {

    return offers.filter(m =>
        (pageFilter.commodities.length === 0 || pageFilter.commodities.some(c => c.id === m.deliveryPeriod.commodity.id)) &&
        (pageFilter.locations.length === 0 || m.location == null || pageFilter.locations.some(x => x.id === m.location.id) &&
            (pageFilter.traders.length === 0 || pageFilter.traders.some(x => x.userId === m.trader.userId)) &&
            (pageFilter.willSell == null || m.customerWillSell === pageFilter.willSell)));
}

export interface OfferHedgeSettings {
    fcmAccount: Nullish<FcmAccount>;
    numberOfContracts: number;
    maxShow?: number;
};

export function getDefaultHedgingSettings(
    merchant: Merchant,
    offer: NullishPartial<Offer>,
    positionSettings: PositionSetting[],
    hedgingSettings: HedgingSettings[],
    fcmAccounts: FcmAccount[]): OfferHedgeSettings {

    if (offer.order != null) {
        //Use the current settings
        return {
            fcmAccount: fcmAccounts.find(m => m.accountNumber === offer.order?.fcmAccountNumber),
            numberOfContracts: offer.order.quantity,
            maxShow: offer.order.maxShow
        };
    }

    const result: OfferHedgeSettings = { fcmAccount: null, numberOfContracts: 0 };
    let fcmAccountId: Nullish<number>;

    const positionSetting = getPositionSetting(positionSettings, offer);
    //AUT-2780 Use L&S FCM account for cash offers
    if (offerTypeHelper.isCash(offer.type) && !merchant.enableAutoPosition && merchant.enableAutoPosition) {
        fcmAccountId = positionSetting?.fcmAccount?.id;
    }

    const hedgingSetting = findHedgeSetting(hedgingSettings, offer);
    if (fcmAccountId == null && hedgingSetting != null) {
        fcmAccountId = hedgingSetting.useFcmAccountFromPosition === true ? positionSetting?.fcmAccount?.id : hedgingSetting.fcmAccountId;
    }

    const defaultFcmAccount = fcmAccounts.find(fa => fa.id === fcmAccountId);
    result.fcmAccount = defaultFcmAccount;
    result.numberOfContracts = calculateNumberOfContracts(offer.commodity, offer.quantity, Boolean(offer.useMini), hedgingSetting, positionSetting);

    return result;
}

export function getPositionSetting(positionSettings: PositionSetting[], offer: NullishPartial<Offer>): Nullish<PositionSetting> {
    if (offer.positionSetting?.id != null) return positionSettings.find(m => m.id === offer.positionSetting?.id);

    return positionSettings.find(x => x.commodity.id === (offer.commodity ?? offer.deliveryPeriod?.commodity)?.id &&
        x.cropYear.id === offer.deliveryPeriod?.cropYear?.id &&
        x.positionGroup?.id === offer.location?.positionGroup?.id);
}

export function getCurrency(offerType: Nullish<OfferTypeEnum>, commodity: Nullish<Commodity>) {
    return (commodity ? (offerTypeHelper.isHta(offerType) ? commodity.baseCurrency : commodity.localCurrency) : null);
}